import React, { useState, useEffect } from 'react'
import { Plus, RefreshCw, Eye, Edit, Trash2, Filter, ChevronDown, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import targetService from '../../../services/targetService'
import CreateTargetModal from '../../modals/CreateTargetModal'
import EditTargetModal from '../../modals/EditTargetModal'
import ViewTargetModal from '../../modals/ViewTargetModal'
import DeleteConfirmModal from '../../modals/DeleteConfirmModal'

const Targets = () => {
  const [targets, setTargets] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedTarget, setSelectedTarget] = useState(null)
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    month: ''
  })

  useEffect(() => {
    loadTargets()
  }, [])

  const loadTargets = async () => {
    try {
      setLoading(true)
      const response = await targetService.getAllTargets()
      console.log('Load targets response:', response) // Debug log
      if (response.success) {
        const targets = response.data?.targets || []
        console.log('Setting targets:', targets) // Debug log
        setTargets(targets)
      } else {
        console.error('Failed to load targets:', response.message)
        toast.error('Failed to load targets')
      }
    } catch (error) {
      console.error('Error loading targets:', error)
      toast.error('Failed to load targets')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTarget = async (targetData) => {
    try {
      const response = await targetService.createTarget(targetData)
      if (response.success) {
        await loadTargets() // Reload targets list
        setShowCreateModal(false)
        toast.success('Target created successfully!')
      } else {
        console.error('Failed to create target:', response.message)
        toast.error(response.message || 'Failed to create target')
      }
    } catch (error) {
      console.error('Error creating target:', error)
      toast.error('Failed to create target')
    }
  }

  const handleEditTarget = async (targetData) => {
    try {
      const response = await targetService.updateTarget(selectedTarget._id, targetData)
      if (response.success) {
        await loadTargets() // Reload targets list
        setShowEditModal(false)
        setSelectedTarget(null)
        toast.success('Target updated successfully!')
      } else {
        console.error('Failed to update target:', response.message)
        toast.error(response.message || 'Failed to update target')
      }
    } catch (error) {
      console.error('Error updating target:', error)
      toast.error('Failed to update target')
    }
  }

  const handleDeleteTarget = async () => {
    if (!selectedTarget) return
    
    try {
      const response = await targetService.deleteTarget(selectedTarget._id)
      if (response.success) {
        await loadTargets() // Reload targets list
        setShowDeleteModal(false)
        setSelectedTarget(null)
        toast.success('Target deleted successfully!')
      } else {
        console.error('Failed to delete target:', response.message)
        toast.error(response.message || 'Failed to delete target')
      }
    } catch (error) {
      console.error('Error deleting target:', error)
      toast.error('Failed to delete target')
    }
  }

  const openDeleteModal = (target) => {
    setSelectedTarget(target)
    setShowDeleteModal(true)
  }

  const openViewModal = (target) => {
    setSelectedTarget(target)
    setShowViewModal(true)
  }

  const openEditModal = (target) => {
    setSelectedTarget(target)
    setShowEditModal(true)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount).replace('LKR', 'Rs.')
  }

  const formatMonth = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'long',
      year: 'numeric' 
    })
  }

  const getStatusBadge = (status) => {
    const statusStyles = {
      achieved: 'bg-green-100 text-green-800 border-green-200',
      not_achieved: 'bg-red-100 text-red-800 border-red-200',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    }
    
    const statusLabels = {
      achieved: 'Achieved',
      not_achieved: 'Not Achieved', 
      in_progress: 'In Progress'
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[status] || statusStyles.not_achieved}`}>
        {statusLabels[status] || 'Not Achieved'}
      </span>
    )
  }

  // Filter targets based on search and filter criteria
  const filteredTargets = targets.filter(target => {
    const searchMatch = !filters.search || 
      target.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      target.type.toLowerCase().includes(filters.search.toLowerCase()) ||
      target.status.toLowerCase().includes(filters.search.toLowerCase())
    
    const typeMatch = !filters.type || target.type === filters.type
    const statusMatch = !filters.status || target.status === filters.status
    const monthMatch = !filters.month || 
      new Date(target.targetMonth).toISOString().slice(0, 7) === filters.month
    
    return searchMatch && typeMatch && statusMatch && monthMatch
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Targets</h2>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadTargets}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Targets
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filter Targets
          </h3>
          <div className="flex items-center space-x-3">
            {(filters.search || filters.type || filters.status || filters.month) && (
              <button
                onClick={() => setFilters({ search: '', type: '', status: '', month: '' })}
                className="inline-flex items-center px-4 py-2 border border-orange-400 text-orange-500 rounded-lg hover:bg-orange-50 transition-all duration-200 text-sm font-medium transform hover:scale-105"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search targets..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">All Types</label>
            <div className="relative">
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none pr-10"
              >
                <option value="">All Types</option>
                <option value="sales">Sales</option>
                <option value="orders">Orders</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">All Status</label>
            <div className="relative">
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none pr-10"
              >
                <option value="">All Status</option>
                <option value="achieved">Achieved</option>
                <option value="not_achieved">Not Achieved</option>
                <option value="in_progress">In Progress</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select month</label>
            <input
              type="month"
              value={filters.month}
              onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>
      </div>

      {/* Targets Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Targets</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-2"></div>
              <p className="text-gray-500">Loading targets...</p>
            </div>
          </div>
        ) : filteredTargets.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No targets found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bonus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    No of emp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTargets.map((target) => (
                  <tr key={target._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {target.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatMonth(target.targetMonth)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                      {target.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(target.targetValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(target.bonusAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {target.employees?.length || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(target.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => openViewModal(target)}
                        className="text-gray-500 hover:text-gray-700 p-1"
                        title="View target"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(target)}
                        className="text-gray-500 hover:text-gray-700 p-1"
                        title="Edit target"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(target)}
                        className="text-gray-500 hover:text-gray-700 p-1"
                        title="Delete target"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateTargetModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTarget}
        />
      )}

      {showEditModal && selectedTarget && (
        <EditTargetModal
          isOpen={showEditModal}
          target={selectedTarget}
          onClose={() => {
            setShowEditModal(false)
            setSelectedTarget(null)
          }}
          onSubmit={handleEditTarget}
        />
      )}

      {showViewModal && selectedTarget && (
        <ViewTargetModal
          isOpen={showViewModal}
          target={selectedTarget}
          onClose={() => {
            setShowViewModal(false)
            setSelectedTarget(null)
          }}
        />
      )}

      {showDeleteModal && selectedTarget && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedTarget(null)
          }}
          onConfirm={handleDeleteTarget}
          itemName={selectedTarget.name}
          itemType="Target"
        />
      )}
    </div>
  )
}

export default Targets
